import { createRouter } from "vue-router";
import AuthLayout from "@/layouts/Auth/AuthLayout.vue";
import MspLayout from "@/layouts/Msp/MspLayout.vue";
import CustomerLayout from "@/layouts/Customer/CustomerLayout.vue";
import SystemLayout from "@/layouts/System/SystemLayout.vue";
import mspRoutes from "./routes/mspRoutes";
import authRoutes from "./routes/authRoutes";
import systemRoutes from "./routes/systemRoutes";
import ProfileLayout from "@/layouts/Profile/ProfileLayout.vue";
import profileRoutes from "./routes/profileRoutes";
import { createWebHistory } from "vue-router";
import customerRoutes from "./routes/customerRoutes";
import { store } from "@/store";
import { $userPrefs } from "@/plugins/userPrefs";
import { isTokenValid } from "@/utils/auth";
import { UUID_SEGMENT_PATTERN } from "@/constants/regex";
import * as Sentry from "@sentry/vue";

const getOrgUuid = () =>
  store.getters["authentication/selectedCustomer"]?.uuid ||
  store.state?.authentication?.selectedCustomer?.uuid ||
  store.getters["authentication/selectedMsp"]?.uuid ||
  store.state?.authentication?.selectedMsp?.uuid ||
  store.getters["authentication/selectedAccount"]?.uuid ||
  store.state?.authentication?.selectedAccount?.uuid ||
  null;

const normalizeSuffix = (fullPath, legacyBase) => {
  if (!fullPath.startsWith(legacyBase)) return "";
  const suffix = fullPath.slice(legacyBase.length);
  return suffix.startsWith("/") ? suffix : suffix ? `/${suffix}` : "";
};

const buildScopedRedirect = (orgType, to, overrideSuffix = null) => {
  const orgUuid = getOrgUuid();
  if (!orgUuid) {
    if (import.meta.env.DEV) {
    }
    if (Sentry?.captureMessage) {
      Sentry.captureMessage("Missing orgUuid for legacy path redirect", {
        level: "warning",
        extra: { orgType, fullPath: to.fullPath },
      });
    }
    return null;
  }

  return {
    path: `/${orgType}/${orgUuid}${overrideSuffix ?? normalizeSuffix(to.fullPath, `/${orgType}`)}`,
    query: to.query,
    hash: to.hash,
  };
};

const routes = [
  {
    path: "/",
    redirect: {
      name: "auth-sign-in",
    },
  },
  // Legacy single paths → scoped equivalents
  {
    path: "/msp/dashboard",
    redirect: (to) =>
      buildScopedRedirect("msp", to, "/dashboard") || {
        path: to.fullPath,
        query: to.query,
        hash: to.hash,
      },
  },
  {
    path: "/customer/dashboard",
    redirect: (to) =>
      buildScopedRedirect("customer", to, "/dashboard") || {
        path: to.fullPath,
        query: to.query,
        hash: to.hash,
      },
  },
  {
    path: "/system/dashboard",
    redirect: (to) =>
      buildScopedRedirect("system", to, "/dashboard") || {
        path: to.fullPath,
        query: to.query,
        hash: to.hash,
      },
  },
  {
    path: "/customer/email_security",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      return {
        path: `/customer/${orgUuid}/email_security/overview`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/customer/email_security/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/customer/${orgUuid}/email_security${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  // Mis-scoped MSP configuration fallback (missing email_security segment)
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})/configuration`,
    redirect: (to) => ({
      path: `/msp/${to.params.orgUuid}/email_security/configuration`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})/configuration/:rest(.*)`,
    redirect: (to) => ({
      path: `/msp/${to.params.orgUuid}/email_security/configuration/${to.params.rest}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/customer/settings",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      return {
        path: `/customer/${orgUuid}/settings/administrators`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/customer/settings/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/customer/${orgUuid}/settings${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/system/settings",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      return {
        path: `/system/${orgUuid}/settings/saml-sso`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/system/settings/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { path: to.fullPath, query: to.query, hash: to.hash };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/system/${orgUuid}/settings${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/auth",
    component: AuthLayout,
    children: authRoutes,
  },

  {
    path: "/sso/callback",
    name: "ssoCalback",
    component: () => import("@/views/Auth/Sso"),
  },
  {
    path: "/azure/callback",
    name: "azureCalback",
    component: () => import("@/views/Auth/Callback"),
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("@/views/Auth/Logout"),
  },
  // New scoped bases
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: MspLayout,
    name: "msp-root",
    children: mspRoutes,
  },
  // Legacy MSP paths without orgUuid -> redirect to scoped equivalent
  {
    path: "/msp/:rest(.*)",
    redirect: (to) =>
      buildScopedRedirect("msp", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      },
  },
  {
    path: `/customer/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: CustomerLayout,
    children: customerRoutes,
  },
  {
    path: `/system/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: SystemLayout,
    children: systemRoutes,
  },

  // Legacy bases redirect to scoped versions (orgUuid inferred later)
  {
    path: "/msp",
    redirect: (to) => ({
      ...(buildScopedRedirect("msp", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: "/customer",
    redirect: (to) => ({
      ...(buildScopedRedirect("customer", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: "/system",
    redirect: (to) => ({
      ...(buildScopedRedirect("system", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: `/:organisationUuid(${UUID_SEGMENT_PATTERN})/:rest(.*)?`,
    name: "org-short-link",
    component: () => import("@/views/Auth/OrgRedirect/index.vue"),
  },
  {
    path: "/profile",
    component: ProfileLayout,
    children: profileRoutes,
  },
  {
    path: "/:catchAll(.*)",
    redirect: (to) => ({
      name: "auth-sign-in",
      query: { redirect: to.fullPath },
    }),
  },
];

const historyBase = import.meta.env.BASE_URL || "/";

export const router = createRouter({
  history: createWebHistory(historyBase),
  linkActiveClass: "active",
  routes,
});

const publicRouteNames = [
  "auth-sign-in",
  "auth-password-reset",
  "auth-password-set",
  "ssoCalback",
  "azureCalback",
  "logout",
];

router.beforeEach((to, _from, next) => {
  if (to.name && publicRouteNames.includes(to.name)) {
    const token =
      store.getters["authentication/token"]?.access_token ||
      $userPrefs.getCurrentToken();

    // If user has valid token and tries to access auth pages, redirect to landing page
    if (isTokenValid(token) && to.name === "auth-sign-in") {
      const orgUuid = getOrgUuid();
      const accountType =
        store.state.authentication?.selectedAccount?.type_name;

      if (orgUuid) {
        if (accountType === "System") {
          return next({ path: `/system/${orgUuid}/dashboard` });
        }
        if (accountType === "MSP") {
          return next({ path: `/msp/${orgUuid}/dashboard` });
        }
        return next({ path: `/customer/${orgUuid}/dashboard` });
      }
    }

    return next();
  }

  const token =
    store.getters["authentication/token"]?.access_token ||
    $userPrefs.getCurrentToken();

  if (isTokenValid(token)) {
    return next();
  }

  return next({
    name: "auth-sign-in",
    query: { redirect: to.fullPath },
  });
});

export default {};
